import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  ColumnLayout,
  Header,
  Box,
  Container,
} from "@amzn/awsui-components-react-v3";
import {
  FieldGroupDto,
  FieldSchemaDto,
} from "@amzn/crowd-front-end-common-library";
import WizardFooter from "../../wizard/WizardFooter/WizardFooter";
import { FormContextProvider } from "../../forms";
import moveTeamStyle from "../moveTeam.module.css";
import "../../forms/FormGroupSection/FormGroupSection.module.css";
import { MoveTeamContext } from "../MoveTeamContext";
import { useWizardWorkflowContext } from "../../wizard/Wizard/WizardWorkflowContextProvider";
import { CustomStepInjectedProps } from "../../wizard/WizardStep/WizardStep";
import FormFieldRenderer from "../../forms/FormFieldRenderer/FormFieldRenderer";
import { MoveTeamInputStepResult as InputStepResult } from "./MoveTeamInputStepResult";
import "../../common/loader.module.css";
import useMoveTeamValidate from "./useMoveTeamValidate";
import StaticTeamRenderer from "../../common/StaticTeamRenderer";
import { LoadingState } from "../../http";

const MoveTeamInputStep: React.FC<CustomStepInjectedProps<
  Partial<InputStepResult>
>> = ({ stepResult, setStepResult, goNext }): JSX.Element => {
  const {
    currentTeam,
    parentTeamId,
    schema,
    metrics,
    parentTeamName,
    parentTeamPath,
  } = useWizardWorkflowContext<MoveTeamContext>();
  const [localChanges, setLocalChanges] = useState<Partial<InputStepResult>>(
    stepResult
  );

  const flashRef = useRef<HTMLDivElement>(null);

  const updateLocalChanges = useCallback(
    (_: Partial<InputStepResult>, diff: Partial<InputStepResult>) => {
      setLocalChanges(diff);
    },
    []
  );
  const onPassValidation = useCallback(
    (validatedChanges: Partial<InputStepResult>) => {
      setStepResult(validatedChanges);
      goNext();
    },
    [setStepResult, goNext]
  );

  const { execute, loadingState, validationErrors } = useMoveTeamValidate(
    schema,
    parentTeamId,
    parentTeamName,
    localChanges,
    currentTeam,
    metrics,
    onPassValidation
  );

  const onNext = (): void => {
    execute();
  };

  useEffect(() => {
    if (validationErrors && flashRef.current) {
      flashRef.current.scrollIntoView();
    }
  }, [validationErrors]);

  const teamGroup = schema.groups.find(
    (group) => group.groupLabel === "Parent team"
  ) as FieldGroupDto;
  const renderedFieldDefinitions = teamGroup.fieldDefinitions;

  const getFieldRenderer = (field: FieldSchemaDto): JSX.Element => {
    return (
      <div>
        <Box variant="awsui-key-label">Current parent team</Box>
        <StaticTeamRenderer
          teamName={parentTeamName}
          teamPath={parentTeamPath}
        />
        <br />
        <div>
          <FormFieldRenderer key={field.propertyName} field={field} />
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <Container header={<Header>{teamGroup.groupLabel}</Header>}>
        <FormContextProvider<Partial<InputStepResult>>
          metrics={metrics}
          formId="moveTeam-page"
          onFormValueChange={updateLocalChanges}
          validationErrors={validationErrors}
        >
          <ColumnLayout>
            <div data-awsui-column-layout-root="true">
              {renderedFieldDefinitions.map((field) => getFieldRenderer(field))}
            </div>
          </ColumnLayout>
        </FormContextProvider>
      </Container>
      <WizardFooter
        onNext={onNext}
        isLoading={loadingState === LoadingState.LOADING}
      />
    </React.Fragment>
  );
};

export default MoveTeamInputStep;
